import MainLayout from "../../components/layouts/MainLayout"
import React, { useEffect, useState } from "react";
import { EyeFilled, EditFilled, DeleteFilled, SearchOutlined, ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { Table, Input, Select, Button, Tag, Space, message, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Auth } from "../../utils/Auth";
import { DateFormat } from "../../utils/formatDate";
import { ConditionGeneraleApi } from "../../api/conditionGeneraleApi";
import { documentTypes, typeUsers, statutColors, TYPE_USER_LABELS } from "./constants";

// const documentTypes = [
//     { value: 'CGV', label: 'Conditions Générales de Vente' },
//     { value: 'CGA', label: "Conditions Générales d'Achat" },
// ]



const ListConditionsGeneralesView = () => {
    const navigate = useNavigate()
    const abortController = new AbortController()

    const [documents, setDocuments] = useState([])
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(15)

    const [search, setSearch] = useState('')
    const [type, setType] = useState(undefined)
    const [type_user, setTypeUser] = useState(undefined)
    const [statut, setStatut] = useState(undefined)
    const [sortBy, setSortBy] = useState('created_at')
    const [sortDirection, setSortDirection] = useState('desc')

    const buildFilters = (currentPage, currentPerPage) => {
        const filters = {
            page: currentPage,
            per_page: currentPerPage,
            sort_by: sortBy,
            sort_direction: sortDirection,
        }

        if (search) filters.search = search
        if (type) filters.type = type
        if (statut) filters.statut = statut
        if (type_user) filters.type_user = type_user

        return filters
    }

    const handleFetchData = async (currentPage = page, currentPerPage = perPage) => {
        setLoading(true)
        try {
            const filters = buildFilters(currentPage, currentPerPage)
            const res = await ConditionGeneraleApi.getDocuments(filters, abortController.signal)

            if (!res || !res.success) {
                setLoading(false)
                return
            }

            setDocuments(res.data.data ?? [])
            setTotal(res.data.total ?? 0)
            setPage(res.data.current_page ?? currentPage)
            setPerPage(res.data.per_page ?? currentPerPage)
            setLoading(false)
        } catch (error) {
            console.log(error)
            setLoading(false)
            const messages = await error?.messages ?? error?.message

            if (messages instanceof Array)
                return Swal.fire({
                    title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                    text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then(() => {
                    if (messages.join('\n') === 'Unauthenticated') {
                        Auth.removeSessionToken()
                        return navigate(`/login`)
                    }
                });
        }
    }

    const handleSearch = () => handleFetchData(1, perPage)

    const handleReset = () => {
        setSearch('')
        setType(undefined)
        setStatut(undefined)
        setSortBy('created_at')
        setSortDirection('desc')
        setTypeUser(undefined)
        setTimeout(() => handleFetchData(1, perPage), 0)
    }

    const handleTableChange = (pagination) => {
        handleFetchData(pagination.current, pagination.pageSize)
    }

    const handleDetail = (document) => navigate(`/conditions-generales/${document.id}/details`)
    const handleEdit = (document) => navigate(`/conditions-generales/${document.id}/modifier`)
    const handleAdd = () => navigate('/conditions-generales/ajouter')

    const handleDelete = async (document) => {
        try {
            const res = await ConditionGeneraleApi.deleteDocument(document.id, abortController.signal)
            if (res.success) {
                message.success(res.message)
            } else {
                message.error(res.message)
            }
            handleFetchData()
        } catch (error) {
            console.error(error)
            message.error("Échec de la suppression du document");
        }
    }

    const handleTypeUserRender = (typeUser) => TYPE_USER_LABELS[typeUser] ?? '';


    useEffect(() => {
        handleFetchData(1, perPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, sortDirection])

    const columns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Public cible',
            dataIndex: 'type_user',
            key: 'type_user',
            render: (value) => value ? handleTypeUserRender(value) : '-'
        },
        {
            title: 'Titre',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (value) => value ? <Tag color={statutColors[value] || 'default'}>{value}</Tag> : '-',
        },
        {
            title: 'Date de publication',
            dataIndex: 'published_at',
            key: 'published_at',
            render: (value) => value ? DateFormat.formatDate(value) : 'Non publié',
        },
        {
            title: 'Date de création',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value) => DateFormat.formatDate(value),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, document) => (
                <Space>
                    <EyeFilled className="text-green-600 cursor-pointer hover:text-green-800" onClick={() => handleDetail(document)} />
                    <EditFilled className="text-blue-600 cursor-pointer hover:text-blue-800" onClick={() => handleEdit(document)} />
                    <Popconfirm title="Supprimer ce document ?" onConfirm={() => handleDelete(document)} okText="Oui" cancelText="Non">
                        <DeleteFilled className="text-red-600 cursor-pointer hover:text-red-800" />
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    return <MainLayout>
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold">Conditions Générales</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Ajouter un document
                </Button>
            </div>

            <div className="bg-white p-4 rounded shadow mb-4">
                <Space wrap>
                    <Input
                        placeholder="Rechercher un titre"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 220 }}
                    />
                    <Select
                        placeholder="Type de document"
                        value={type}
                        onChange={setType}
                        allowClear
                        style={{ width: 220 }}
                        options={documentTypes}
                    />
                    <Select
                        placeholder="Statut"
                        value={statut}
                        onChange={setStatut}
                        allowClear
                        style={{ width: 180 }}
                        options={[
                            { value: 'BROUILLON', label: 'Brouillon' },
                            { value: 'PUBLIE', label: 'Publié' },
                            { value: 'ARCHIVE', label: 'Archivé' },
                        ]}
                    />
                    <Select
                        value={sortBy}
                        onChange={setSortBy}
                        style={{ width: 220 }}
                        options={[
                            { value: 'created_at', label: 'Trier par date de création' },
                            { value: 'published_at', label: 'Trier par date de publication' },
                        ]}
                    />
                    <Select
                        value={sortDirection}
                        onChange={setSortDirection}
                        style={{ width: 160 }}
                        options={[
                            { value: 'desc', label: 'Plus récent' },
                            { value: 'asc', label: 'Plus ancien' },
                        ]}
                    />
                    <Select
                        placeholder="Public cible"
                        value={type_user}
                        onChange={setTypeUser}
                        allowClear
                        style={{ width: 220 }}
                        options={typeUsers}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Rechercher
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Réinitialiser
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={documents}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: page,
                    pageSize: perPage,
                    total: total,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
            />
        </div>
    </MainLayout>
}

export default ListConditionsGeneralesView
